import type React from "react";
import { useEffect, useState } from "react";
import { MessageSquare, Send, Loader, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  clearCommentError,
  getComments,
  addComment,
  deleteComment,
  commentReceived,
  commentRemoved,
} from "../../store/slices/commentSlice";
import { useSocketEvent, useSocketRoom } from "../../hooks/useSocketEvent";
import type { Comment } from "../../types";
import { useTranslation } from "react-i18next";
interface CommentSectionProps {
  shipmentId?: string;
  token?: string;
  isAuth?: boolean;
  title?: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  shipmentId,
  token,
  isAuth = true,
  title,
}) => {
  const {
    comments,
    loading: commentLoading,
    error: commentError,
  } = useAppSelector((state) => state.comments);
  const { t } = useTranslation("shipments");
  const [newComment, setNewComment] = useState("");
  const dispatch = useAppDispatch();

  // Load comments when component mounts or token changes
  useEffect(() => {
    if (token || shipmentId) {
      const loadComments = async () => {
        try {
          if (token) {
            if (isAuth) {
              await dispatch(
                getComments({ source: "driver", token, isProtected: true }),
              );
            } else {
              await dispatch(getComments({ source: "driver", token }));
            }
          }
          if (shipmentId) {
            await dispatch(
              getComments({ source: "shipment", token: shipmentId }),
            );
          }
        } catch (error) {
          console.error("Failed to load comments:", error);
        }
      };
      loadComments();
    }
  }, [token]);

  /**
   * The shipment whose room this section should join.
   *
   * `shipmentId` is set on the dispatcher views. On DriverView the id arrives
   * as `token`, which is the shipment _id for an authenticated driver but an
   * opaque public-link token otherwise — and public link visitors have no
   * socket at all this phase, so they are excluded rather than sent an id the
   * server would reject.
   */
  const roomShipmentId = shipmentId ?? (isAuth ? token : undefined);

  // Joins on mount, leaves on unmount: the subscription lives exactly as long
  // as this shipment is on screen. That is what keeps comment traffic off every
  // other client — the server emits comments to this room only.
  useSocketRoom("shipment", roomShipmentId);

  useSocketEvent<{ shipmentId: string; comment: Comment }>(
    "shipment:commentAdded",
    (payload) => {
      // The room already scopes this, but a stale membership after a fast
      // navigation could still deliver one for the previous shipment.
      if (roomShipmentId && payload?.shipmentId !== roomShipmentId) return;
      dispatch(commentReceived(payload.comment));
    },
    Boolean(roomShipmentId),
  );

  useSocketEvent<{ shipmentId: string; commentId: string }>(
    "shipment:commentDeleted",
    (payload) => {
      if (roomShipmentId && payload?.shipmentId !== roomShipmentId) return;
      dispatch(commentRemoved(payload.commentId));
    },
    Boolean(roomShipmentId),
  );

  // Handle comment errors
  useEffect(() => {
    if (commentError) {
      toast.error(commentError);
      dispatch(clearCommentError());
    }
  }, [commentError]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      if (token) {
        if (isAuth) {
          await dispatch(
            addComment({
              source: "driver",
              shipmentId: token,
              commentData: { message: newComment },
              isProtected: true,
            }),
          ).unwrap();
        } else {
          await dispatch(
            addComment({
              source: "driver",
              shipmentId: token,
              commentData: { message: newComment },
            }),
          ).unwrap();
        }
        setNewComment("");
        toast.success(t("comments.addedSuccess"));
      }
      if (shipmentId) {
        await dispatch(
          addComment({
            source: "shipment",
            shipmentId: shipmentId,
            commentData: { message: newComment },
          }),
        ).unwrap();
        setNewComment("");
        toast.success(t("comments.addedSuccess"));
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  const handleCommentKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      if (token) {
        if (isAuth) {
          await dispatch(
            deleteComment({
              source: "driver",
              shipmentId: token,
              commentId: commentId,
              isProtected: true,
            }),
          ).unwrap();
        } else {
          await dispatch(
            deleteComment({
              source: "driver",
              shipmentId: token,
              commentId: commentId,
            }),
          ).unwrap();
        }

        toast.success(t("comments.deletedSuccess"));
      }
      if (shipmentId) {
        await dispatch(
          deleteComment({
            source: "shipment",
            shipmentId: shipmentId,
            commentId: commentId,
          }),
        ).unwrap();
        toast.success(t("comments.deletedSuccess"));
      }
    } catch (e) {}
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <MessageSquare className="h-5 w-5 text-gray-600" />
          <span>{title ?? t("comments.title")}</span>
          {comments.length > 0 && (
            <span className="text-sm font-normal text-gray-500">
              ({comments.length})
            </span>
          )}
        </h2>
      </div>

      {/* Comments List */}
      {comments.length > 0 && (
        <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
          {comments.map((comment) => {
            const isDriver = comment.addedBy === "driver";
            const bgColor = isDriver ? "bg-blue-50" : "bg-green-50";
            const borderColor = isDriver
              ? "border-blue-200"
              : "border-green-200";
            const canDelete = isDriver;
            return (
              <div
                key={comment._id}
                className={`flex items-start space-x-3 p-3 ${bgColor} rounded-lg border ${borderColor} relative`}
              >
                {/* Avatar Circle */}
                <div
                  className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                    isDriver
                      ? "bg-blue-600 text-white"
                      : "bg-green-600 text-white"
                  }`}
                >
                  {isDriver ? "D" : "U"}
                </div>

                {/* Comment Content */}
                <div className="flex-1 pr-8">
                  <p className="text-sm text-gray-900 mb-1">
                    {comment.message}
                  </p>
                  <p className="text-xs text-gray-500">
                    {comment.createdAt?.split("T")[0]} -{" "}
                    {comment.createdAt?.split("T")[1].split(".")[0]}
                  </p>
                </div>

                {/* Delete Button - Only for driver's own comments */}
                {token && canDelete && (
                  <button
                    onClick={() => handleDeleteComment(comment._id)}
                    className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-red-600 active:text-red-700 transition-colors rounded"
                    title="Delete comment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {shipmentId && !canDelete && (
                  <button
                    onClick={() => handleDeleteComment(comment._id)}
                    className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-red-600 active:text-red-700 transition-colors rounded"
                    title="Delete comment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Comment Input */}
      <div className="flex space-x-2">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handleCommentKeyPress}
          placeholder={t("comments.placeholder")}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          rows={2}
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim() || commentLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
        >
          {commentLoading ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {commentLoading ? t("comments.sending") : t("comments.send")}
          </span>
        </button>
      </div>
    </div>
  );
};
